import { ENS_EMAIL_DOMAIN } from '@constants';
import {
  Address,
  Organization,
  Practitioner,
  Professional,
  ProfessionalBase,
  ProfessionalDto,
  Telecom,
  TelecomType,
} from '../types';
import { isOrganization } from '../validators';
import { isLifenMssEmail } from './email.utils';

export function getProfessionalAsReference(
  professional: ProfessionalBase | Practitioner | Organization | ProfessionalDto,
): string {
  const resourceType = professional.type;
  const { id } = professional;

  return resourceType && id && `${resourceType}/${id}`;
}

export function isMssTelecom(telecom: { type?: TelecomType }): boolean {
  return telecom.type === TelecomType.MSSANTE;
}

export function isEnsTelecom(telecom: { value?: string }): boolean {
  return !!telecom.value?.match(new RegExp(`^[0-9]+@${ENS_EMAIL_DOMAIN}`));
}

const isActive = (telecom: Telecom) => !telecom.inactive;

export function getPreferredSecuredTelecoms(professional: Professional): Telecom[] {
  return [
    ...professional.telecoms.lifen,
    ...professional.telecoms.mssante,
    ...professional.telecoms.apicrypt,
  ].filter((telecom) => isActive(telecom) && telecom.isContactPreference);
}

/**
 * The telecoms carrying this address when it only exists as inactive telecoms on the professional.
 * Empty if the address is unknown, or if an active telecom carries it too (still usable, nothing to flag).
 */
export function getTelecomsOfInactiveAddress(professional: Professional, value: string): Telecom[] {
  const matching = Object.values(professional.telecoms)
    .flat()
    .filter((telecom) => telecom.value.toLowerCase() === value.toLowerCase());

  return matching.some(isActive) ? [] : matching;
}

export function getMssSanteIfNotPreferred(professional: Professional) {
  const mssante = professional.telecoms.mssante.filter(isActive);

  if (mssante.some((t) => t.isContactPreference)) return [];

  if (isOrganization(professional) && mssante.some((t) => isLifenMssEmail(t.value))) {
    return mssante.filter((t) => isLifenMssEmail(t.value));
  }

  return mssante;
}

interface TelecomsConfig {
  withEmail?: boolean;
}

export function getSendableTelecoms(
  professional: Professional,
  config?: TelecomsConfig,
): Telecom[] {
  return [
    ...professional.telecoms.lifen,
    ...professional.telecoms.mssante,
    ...professional.telecoms.apicrypt,
    ...(config?.withEmail ? professional.telecoms.email : []),
  ].filter(isActive);
}

export function getPreferredAndEnforcedOrSendableTelecoms(
  professional: Professional,
  config?: TelecomsConfig,
) {
  const preferredTelecoms = getPreferredSecuredTelecoms(professional);

  if (preferredTelecoms.length > 0) {
    return [...preferredTelecoms, ...getMssSanteIfNotPreferred(professional)];
  }

  if (isOrganization(professional)) {
    if (professional.telecoms.mssante.filter(isActive).length > 1) {
      return [];
    }
  }

  return getSendableTelecoms(professional, config);
}

export function getPostalContactPreference(professional: Professional): Address[] {
  /*
   * If a professional has a postal contact preference, we
   * have now way to know which one, so we take the first one.
   */
  return professional.hasPostalContactPreference ? professional.address.slice(0, 1) : [];
}
